﻿import React, { useRef, useState } from "react";
import { ImageViewer } from "components/notifications/ImageViewer";
import { MediaViewer } from "components/notifications/MediaViewer";
import PropTypes from "prop-types";
import classNames from 'classnames';
import { ShowHide } from "../layout";
import { Icon, Intent } from "@blueprintjs/core";
import { ImageCropper } from "./ImageCropper";
import { Modal, ModalSize, NotificationToaster } from "../notifications";
import { Button, ButtonSave } from "../buttons";
import { Tooltip } from "../tooltip";
import { MediaPlaceholder } from "./MediaPlaceholder";
import noMediaImage from './no_media.jpg';
import './MediaList.css';
import './DocumentViewer.css';
import './ImageCropper.css';
import './MediaPlaceholder.css';


export function MediaList(
    {
        size = 'medium',
        showMediaCounter = true,
        showPlaceholder = true,
        showFilenames = false,
        allowZoom = false,
        allowAddMedia,
        allowDeleteMedia,
        handleAddMedia,
        handleDeleteMedia,
        loading,
        maxNoOfMedia,
        media,
        mediaTitle,
        renderMediaViewer
    }
) {
    const [showImageModal, setShowImageModal] = useState(false);
    const [showVideoModal, setShowVideoModal] = useState(false);
    const [showPdfModal, setShowPdfModal] = useState(false);
    const [selectedMedia, setSelectedMedia] = useState("");
    const [showImageCropperModal, setShowImageCropperModal] = useState(false);
    const [fileForSave, setFileForSave] = useState(null);
    const [saving, setSaving] = useState(false);
    const total = media.length;
    const cropperRef = useRef();
    const fileInputRef = useRef(null);
    const addButtonDisabled = maxNoOfMedia && media.length >= maxNoOfMedia;
    const [errorImages, setErrorImages] = useState([]);
    const [loadedImages, setLoadedImages] = useState([]);
    const [errorVideos, setErrorVideos] = useState([]);

    const videoExtensions = [".mp4", ".mov"];
    const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".bmp", ".webp"];
    const pdfExtensions = [".pdf"];

    const handleImageLoad = (fileName) => {
        setLoadedImages(prev => [...prev, fileName]);
    };

    const handleImageError = (fileName, event) => {
        event.target.onerror = null;
        event.target.src = noMediaImage;

        setErrorImages(prev => [...prev, fileName]);
    };

    const handleVideoError = (fileName) => {
        setErrorVideos(prev => [...prev, fileName]);
    };

    const isImageLoaded = (fileName) => loadedImages.includes(fileName);
    const isImageErrored = (fileName) => errorImages.includes(fileName);

    const openImageCropperModal = (file) => {
        setFileForSave(file);
        setShowImageCropperModal(true);
    }

    const handleClose = () => {
        setFileForSave(null);
        setShowImageCropperModal(false);
    }

    const handleImageSave = async () => {
        try {
            setSaving(true);

            if (!cropperRef.current) {
                NotificationToaster.show({
                    intent: Intent.DANGER,
                    message: "Image cropper is not ready. Please try again."
                });
                return;
            }

            const croppedImageBlob = await cropperRef.current.getCroppedImage();

            if (!croppedImageBlob) {
                NotificationToaster.show({
                    intent: Intent.DANGER,
                    message: "Failed to crop the image. Please try again."
                });
                return;
            }

            const newFile = new File([croppedImageBlob], fileForSave.name, {
                type: croppedImageBlob.type
            });

            await handleAddMedia(newFile);

            handleClose();

        } catch {
            NotificationToaster.show({
                intent: Intent.DANGER,
                message: "An unexpected error occurred while saving the image."
            });
        } finally {
            setSaving(false);
        }
    };

    const addMediaButton = allowAddMedia ? (
        <>
            <Tooltip content="Add Image" position="top" disabled={addButtonDisabled}>
                <button
                    type="button"
                    className={classNames("medialist-thumbnail-add", size)}
                    disabled={addButtonDisabled}
                    onClick={() => {
                        if (!addButtonDisabled) {
                            fileInputRef.current?.click();
                        }
                    }}
                >
                    <Icon className="medialist-camera-icon" icon="camera" />
                </button>
            </Tooltip>
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                        openImageCropperModal(file);
                        e.target.value = "";
                    }
                }}
            />
        </>
    ) : null;

    const mediaThumbnail = (file) => {

        const url = generateMediaUrl(file);
        const imageHasError = isImageErrored(file.fileName);

        const renderVideo = () => (
            errorVideos.includes(file.fileName) ? (
                <img
                    src={noMediaImage}
                    alt="No video available"
                    className={classNames("medialist-thumbnail inline-item list-table-clickable-header", size)}
                />
            ) : (
                <video
                    onClick={() => onShowMedia(file)}
                    className={classNames("medialist-thumbnail inline-item list-table-clickable-header", size)}
                    src={url}
                    muted
                    playsInline
                    preload="metadata"
                    style={{ objectFit: 'cover' }}
                    onError={() => handleVideoError(file.fileName)}
                />
            )
        );

        const renderImage = () => (
            <button
                onClick={() => !isImageErrored(file.fileName) && onShowMedia(file)}
                className="medialist-image-button"
                aria-label="View media"
            >
                <img
                    alt=""
                    className={classNames("medialist-thumbnail inline-item list-table-clickable-header", size)}
                    src={url}
                    onLoad={() => handleImageLoad(file.fileName)}
                    onError={(e) => handleImageError(file.fileName, e)}
                />
            </button>
        );

        const renderOtherMedia = () => {

            let icon = isPdf(file) ? "fa fa-file-pdf-o" : "fa fa-file-o";

            return (
                <button
                    className={classNames("medialist-thumbnail medialist-image-button inline-item", size)}
                    onClick={() => onShowMedia(file)}
                >
                    <i className={icon}></i>
                </button>
            );
        }

        const renderMedia = () => {

            if (isVideo(file)) {
                return renderVideo()
            } else if (isImage(file)) {
                return renderImage()
            } else {
                return renderOtherMedia();
            }

        }

        const renderFilename = () => (
            <div className="medialist-filename-holder">
                <Tooltip position="bottom" content={file.fileName}>
                    <div className="medialist-filename">
                        {file.fileName}
                    </div>
                </Tooltip>
            </div>

        );

        return (
            <div
                key={file.fileName}
                className={classNames("medialist-thumbnail-wrapper", size)}
            >
                {renderMedia()}
                {showFilenames && renderFilename()}
                {allowDeleteMedia && !imageHasError && isImageLoaded(file.fileName) && (
                    <div className="medialist-delete-button-wrapper ">
                        <Tooltip content="Delete">
                            <button
                                className="medialist-delete-button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteMedia(file);
                                }}
                                aria-label="Delete media"
                            >
                                <Icon icon="small-cross" />
                            </button>
                        </Tooltip>
                    </div>
                )}
            </div>
        );
    }

    let mediaList = [];

    if (media.length > 0) {
        mediaList = [
            ...media.map((file, index) => mediaThumbnail(file)),
            ...(allowAddMedia ? [<div key="add-media-btn">{addMediaButton}</div>] : [])
        ];
    } else if (!allowAddMedia && showPlaceholder) {
        mediaList = [
            <MediaPlaceholder key="placeholder" size={size} message="No media available" />
        ];
    } else {
        mediaList = [addMediaButton];
    }

    const mediaWithCounter = (
        <div className={classNames(
            "medialist-media-items",
            { "medialist--loading": loading }
        )}>
            {mediaList}
            {showMediaCounter && (<div className="medialist-media-counter">{`${total}/${!maxNoOfMedia ? total : maxNoOfMedia}`}</div>)}
        </div>
    );

    let imageData = [
        {
            header: mediaTitle,
            value: mediaList.length > 0 ? mediaWithCounter : "Media unavailable",
            key: 'media'
        },
    ];

    function onShowMedia(file) {

        var selectedFile = generateMediaUrl(file);

        setSelectedMedia(selectedFile);

        if (isVideo(file)) {
            setShowVideoModal(true);
        } else if (isImage(file)) {
            setShowImageModal(true);
        } else if (isPdf(file)) {
            setShowPdfModal(true);
        } else {
            const a = document.createElement('a');
            a.href = selectedFile;
            a.click();
        }
    }

    function onHideMedia() {
        setShowImageModal(false);
        setShowVideoModal(false);
        setShowPdfModal(false);
    }

    function isVideo(file) {
        return videoExtensions.some(ext => file?.fileName?.toLowerCase()?.endsWith(ext));
    }

    function isImage(file) {
        return imageExtensions.some(ext => file?.fileName?.toLowerCase()?.endsWith(ext));
    }

    function isPdf(file) {
        return pdfExtensions.some(ext => file?.fileName?.toLowerCase()?.endsWith(ext));
    }

    function generateMediaUrl(file) {
        return file.url != null ? file.url : `https://${file?.storageName}.blob.core.windows.net/${file.containerName}/${file?.fileName}?${file?.token}`;
    }

    return (
        <div>
            <div className="medialist-table-columns">
                {imageData?.map(d => {
                    return (
                        <div className={classNames("medialist-table-field form-field", { "medialist-table-no-value": !d.value })} key={d.key}>
                            {mediaTitle ? (<h4 className="medialist-table-field-header">{d.header}</h4>) : null}
                            <ShowHide
                                evaluator={!!d.value}
                                show={
                                    <span className="medialist-table-field-value">
                                        {d.value}
                                    </span>
                                }
                            />
                        </div>
                    );
                })}
            </div>
            {renderMediaViewer ? renderMediaViewer({ selectedMedia, showMedia: showImageModal || showVideoModal, onHideMedia }) :
                (<>
                    <ImageViewer
                        isOpen={showImageModal}
                        onClose={onHideMedia}
                        mediaSource={selectedMedia}
                        allowZoom={allowZoom}
                        imageStyle={{
                            maxWidth: "90vw",
                            maxHeight: "80vh",
                            objectFit: "contain",
                        }}
                    />
                    <MediaViewer
                        isOpen={showVideoModal}
                        onClose={onHideMedia}
                        mediaSource={selectedMedia}
                        videoStyle={{ width: 'auto', maxWidth: '100%', maxHeight: '80vh', borderRadius: '8px' }}
                    />
                    <Modal
                        isOpen={showPdfModal}
                        size={ModalSize.LARGE}
                        onClose={onHideMedia}
                    >
                        <iframe title="View Media PDF" style={{ width: '100%', height: 800, border: 0 }} src={selectedMedia} />
                    </Modal>
                </>)}
            <Modal
                isOpen={showImageCropperModal}
                clickOutsideClose={false}
                showCloseButton={false}
                title={'Add Media'}
                onClose={handleClose}
                size={ModalSize.SMALL}
                className="medialist-image-cropper-modal"
                onOpened={() => cropperRef.current?.refreshAspect()}
                footer={
                    <div className="bp6-dialog-footer-actions">
                        <Button
                            intent="secondary"
                            text="Cancel"
                            onClick={handleClose}
                            disabled={loading || saving}
                            id="cancel-btn"
                        />
                        <ButtonSave intent="primary" loading={loading} text="Save" disabled={saving} simpleDisabled={loading} onClick={handleImageSave} id="save-btn" />
                    </div>
                }
            >
                <ImageCropper file={fileForSave} ref={cropperRef} saving={saving} />
            </Modal>
        </div>
    );
}

MediaList.propTypes = {
    media: PropTypes.array.isRequired,
    mediaTitle: PropTypes.string,
    maxNoOfMedia: PropTypes.number,
    showMediaCounter: PropTypes.bool,
    showPlaceholder: PropTypes.bool,
    loading: PropTypes.bool,
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    allowAddMedia: PropTypes.bool,
    allowDeleteMedia: PropTypes.bool,
    allowZoom: PropTypes.bool,
    showFilenames: PropTypes.bool,
    handleAddMedia: (props, propName, componentName) => {
        if (props.allowAddMedia && typeof props[propName] !== 'function') {
            return new Error(
                `Invalid prop \`${propName}\` supplied to \`${componentName}\`: required when allowAddMedia is true.`
            );
        }
        return null;
    },
    handleDeleteMedia: (props, propName, componentName) => {
        if (props.allowDeleteMedia && typeof props[propName] !== 'function') {
            return new Error(
                `Invalid prop \`${propName}\` supplied to \`${componentName}\`: required when allowDeleteMedia is true.`
            );
        }
        return null;
    },
    renderMediaViewer: PropTypes.func,
};